<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Capacitor } from '@capacitor/core';
import { IonContent, IonFooter, IonHeader, IonModal } from '@ionic/vue';
import { closeOutline, pencilOutline } from 'ionicons/icons';
import { WORKOUT_EXIT_REASONS, WorkoutExitReason } from '@/api/workout/types/exit-reason';
import { canSubmitEarlyExit, resolveExitReasonText, toggleExitReason } from '../utils/early-exit-reasons';
import {
  readEarlyExitViewportSnapshot,
  resolveEarlyExitModalViewportStyle,
  scrollEarlyExitRowIntoContent,
  waitForEarlyExitKeyboardSettleIfNeeded,
  type EarlyExitModalViewportStyle,
} from '../utils/early-exit-keyboard-layout';

const props = defineProps<{
  open: boolean;
  submitting?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  continue: [];
  submit: [payload: { exitReasons: WorkoutExitReason[]; exitReasonText?: string }];
  quit: [];
}>();

const { t } = useI18n();
const selectedReasons = ref<WorkoutExitReason[]>([]);
/** Which leave action is in flight, so only that button shows the spinner. */
const pendingAction = ref<'submit' | 'quit' | null>(null);
const otherText = ref('');
const otherInputRef = ref<HTMLInputElement | null>(null);
const otherRowRef = ref<HTMLElement | null>(null);
const contentRef = ref<{ $el?: unknown } | null>(null);
const viewportStyle = ref<EarlyExitModalViewportStyle>(
  resolveEarlyExitModalViewportStyle(typeof window === 'undefined' ? 0 : window.innerHeight)
);
let scrollRequestId = 0;

const setOtherInputRef = (el: unknown) => {
  otherInputRef.value = el instanceof HTMLInputElement ? el : null;
};

const setOtherRowRef = (el: unknown) => {
  otherRowRef.value = el instanceof HTMLElement ? el : null;
};

const readVisualViewport = () =>
  window.visualViewport ? { offsetTop: window.visualViewport.offsetTop, height: window.visualViewport.height } : null;

const cancelPendingOtherScroll = () => {
  scrollRequestId += 1;
};

const requestOtherRowVisible = () => {
  const requestId = ++scrollRequestId;
  void (async () => {
    await waitForEarlyExitKeyboardSettleIfNeeded(Capacitor.getPlatform() === 'ios', {
      readSnapshot: () => readEarlyExitViewportSnapshot(window.innerHeight, readVisualViewport()),
      raf: cb => requestAnimationFrame(cb),
    });
    if (requestId !== scrollRequestId) return;
    await nextTick();
    if (requestId !== scrollRequestId) return;
    const content = contentRef.value as {
      $el?: {
        getScrollElement?: () => Promise<HTMLElement>;
        scrollToPoint?: (x: number, y: number, duration?: number) => Promise<void>;
      };
    } | null;
    await scrollEarlyExitRowIntoContent(otherRowRef.value, content?.$el ?? content);
  })();
};

const syncViewport = () => {
  viewportStyle.value = resolveEarlyExitModalViewportStyle(window.innerHeight, readVisualViewport());
  if (otherInputRef.value && document.activeElement === otherInputRef.value) {
    requestOtherRowVisible();
  }
};

const attachViewport = () => {
  syncViewport();
  window.visualViewport?.addEventListener('resize', syncViewport);
  window.visualViewport?.addEventListener('scroll', syncViewport);
  window.addEventListener('resize', syncViewport);
};

const detachViewport = () => {
  cancelPendingOtherScroll();
  window.visualViewport?.removeEventListener('resize', syncViewport);
  window.visualViewport?.removeEventListener('scroll', syncViewport);
  window.removeEventListener('resize', syncViewport);
  viewportStyle.value = resolveEarlyExitModalViewportStyle(typeof window === 'undefined' ? 0 : window.innerHeight);
};

const focusOtherInput = async () => {
  await nextTick();
  const input = otherInputRef.value;
  if (input && typeof input.focus === 'function') {
    input.focus();
  }
  requestOtherRowVisible();
};

watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      selectedReasons.value = [];
      otherText.value = '';
      pendingAction.value = null;
      attachViewport();
      return;
    }
    detachViewport();
  }
);

onBeforeUnmount(detachViewport);

const modalViewportVars = computed(() => ({
  '--height': viewportStyle.value.height,
  '--early-exit-visible-top': viewportStyle.value.top,
}));

const isSelected = (reason: WorkoutExitReason) => selectedReasons.value.includes(reason);

const onToggle = async (reason: WorkoutExitReason) => {
  selectedReasons.value = toggleExitReason(selectedReasons.value, reason);
  if (reason === WorkoutExitReason.Other && selectedReasons.value.includes(WorkoutExitReason.Other)) {
    await focusOtherInput();
  }
};

const onClearOtherText = () => {
  otherText.value = '';
  void focusOtherInput();
};

const onClose = () => {
  if (props.submitting) return;
  emit('close');
};

const onContinue = () => {
  if (props.submitting) return;
  emit('continue');
};

const onSubmit = () => {
  if (props.submitting || !canSubmitEarlyExit(selectedReasons.value)) return;
  pendingAction.value = 'submit';
  emit('submit', {
    exitReasons: [...selectedReasons.value],
    exitReasonText: resolveExitReasonText(selectedReasons.value, otherText.value),
  });
};

const onQuit = () => {
  if (props.submitting) return;
  pendingAction.value = 'quit';
  emit('quit');
};
</script>

<template>
  <ion-modal :is-open="open" :backdrop-dismiss="false" class="early-exit-modal" :style="modalViewportVars">
    <ion-header class="ion-no-border early-exit-header-wrap">
      <div class="early-exit-header">
        <h1 class="early-exit-title">{{ t('workout-early-exit.title') }}</h1>
        <button type="button" class="early-exit-close" :disabled="submitting" aria-label="Close" @click="onClose">
          <ion-icon :icon="closeOutline" />
        </button>
      </div>
    </ion-header>

    <ion-content ref="contentRef" class="early-exit-scroll" :scroll-y="true">
      <p class="early-exit-subtitle">{{ t('workout-early-exit.subtitle') }}</p>

      <div class="early-exit-options">
        <template v-for="reason in WORKOUT_EXIT_REASONS" :key="reason">
          <button
            v-if="reason !== WorkoutExitReason.Other || !isSelected(WorkoutExitReason.Other)"
            type="button"
            class="early-exit-option"
            :class="{ active: isSelected(reason) }"
            :data-reason="reason"
            @click="onToggle(reason)"
          >
            <span>{{ t(`workout-early-exit.reasons.${reason}`) }}</span>
            <ion-icon v-if="reason === WorkoutExitReason.Other" :icon="pencilOutline" class="early-exit-option-icon" />
          </button>

          <div
            v-else
            :ref="setOtherRowRef"
            class="early-exit-option early-exit-option--other-input active"
            data-reason="other"
            data-testid="early-exit-other-row"
          >
            <input
              :ref="setOtherInputRef"
              v-model="otherText"
              class="early-exit-other-input"
              type="text"
              dir="auto"
              maxlength="500"
              :placeholder="t('workout-early-exit.other-placeholder')"
              data-testid="early-exit-other-text"
              @focus="requestOtherRowVisible"
            />
            <button
              v-if="otherText"
              type="button"
              class="early-exit-clear"
              aria-label="Clear"
              data-testid="early-exit-other-clear"
              @click="onClearOtherText"
            >
              <ion-icon :icon="closeOutline" />
            </button>
          </div>
        </template>
      </div>
    </ion-content>

    <ion-footer class="ion-no-border early-exit-footer" :class="{ 'keyboard-open': viewportStyle.keyboardOpen }">
      <div class="early-exit-actions">
        <base-button class="early-exit-continue" color="primary" :disabled="submitting" @click="onContinue">
          {{ t('workout-early-exit.continue') }}
        </base-button>
        <base-button
          fill="outline"
          class="early-exit-submit"
          :disabled="!canSubmitEarlyExit(selectedReasons) || submitting"
          :loading="submitting && pendingAction === 'submit'"
          @click="onSubmit"
        >
          {{ t('workout-early-exit.submit') }}
        </base-button>
        <base-button
          fill="clear"
          class="early-exit-quit"
          :disabled="submitting"
          :loading="submitting && pendingAction === 'quit'"
          @click="onQuit"
        >
          {{ t('workout-early-exit.quit') }}
        </base-button>
      </div>
    </ion-footer>
  </ion-modal>
</template>

<style lang="scss">
@import '@/scss/setup/_functions.scss';

.early-exit-modal {
  --width: 100%;
  --height: 100%;
  --background: #121212;
  --box-shadow: none;
  align-items: flex-start;
}

.early-exit-modal::part(content) {
  margin-top: var(--early-exit-visible-top, 0px);
}

.early-exit-header-wrap {
  padding: calc(var(--ion-safe-area-top, 0px) + 20px) 20px 0;
  background: #121212;
  box-shadow: none;
}

.early-exit-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.early-exit-title {
  margin: 0;
  flex: 1;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.15;
  color: #ffffff;
}

.early-exit-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 28px;
}

.early-exit-subtitle {
  margin: 12px 0 36px;
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.4;
  color: #b0b0b0;
}

.early-exit-scroll {
  --background: #121212;
  --padding-start: 20px;
  --padding-end: 20px;
  --keyboard-offset: 0px;
}

.early-exit-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
}

.early-exit-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 56px;
  padding: 16px 20px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #1c1c1c;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  text-align: start;

  &.active {
    border-color: var(--ion-color-primary);
  }
}

.early-exit-option-icon {
  font-size: 20px;
  color: #d0d0d0;
}

.early-exit-option--other-input {
  padding: 8px 16px 8px 20px;
}

.early-exit-other-input {
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 1rem;
  outline: none;
}

.early-exit-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 22px;
}

.early-exit-footer {
  background: #121212;
  box-shadow: none;
}

.early-exit-footer::before {
  display: none;
}

.early-exit-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px calc(16px + var(--ion-safe-area-bottom, 0px));
}

.early-exit-footer.keyboard-open .early-exit-actions {
  padding-bottom: 16px;
}

.early-exit-continue {
  --background: var(--ion-color-primary);
  --color: #202020;
  letter-spacing: 0;
}

.early-exit-submit {
  --border-color: #ffffff;
  --color: #ffffff;
  --background: transparent;
  letter-spacing: 0;
}

.early-exit-quit {
  --color: var(--ion-color-tertiary);
  --background: transparent;
  letter-spacing: 0;
}
</style>
